import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@shared/lib/cn';
import { Icon } from './Icon';

interface ChoiceBaseProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
  description?: ReactNode;
}

/** Box always at the start side, label after it (design system §4.10). Never pre-ticked for consents. */
export const Checkbox = forwardRef<HTMLInputElement, ChoiceBaseProps>(function Checkbox(
  { label, description, className, id: givenId, ...rest },
  ref,
) {
  const autoId = useId();
  const id = givenId ?? `c${autoId.replace(/:/g, '')}`;
  const descId = description ? `${id}-desc` : undefined;
  return (
    <div className={cn('flex min-h-touch-min items-start gap-3 py-1', className)}>
      <input
        ref={ref}
        id={id}
        type="checkbox"
        aria-describedby={descId}
        className="mt-0.5 size-5 shrink-0 cursor-pointer rounded-sm border-2 border-border-strong accent-primary"
        {...rest}
      />
      <div className="flex flex-col">
        <label htmlFor={id} className="cursor-pointer text-md text-primary">
          {label}
        </label>
        {description ? (
          <span id={descId} className="text-sm text-muted">
            {description}
          </span>
        ) : null}
      </div>
    </div>
  );
});

export const Radio = forwardRef<HTMLInputElement, ChoiceBaseProps>(function Radio(
  { label, description, className, id: givenId, ...rest },
  ref,
) {
  const autoId = useId();
  const id = givenId ?? `r${autoId.replace(/:/g, '')}`;
  const descId = description ? `${id}-desc` : undefined;
  return (
    <div className={cn('flex min-h-touch-min items-start gap-3 py-1', className)}>
      <input
        ref={ref}
        id={id}
        type="radio"
        aria-describedby={descId}
        className="mt-0.5 size-5 shrink-0 cursor-pointer accent-primary"
        {...rest}
      />
      <div className="flex flex-col">
        <label htmlFor={id} className="cursor-pointer text-md text-primary">
          {label}
        </label>
        {description ? (
          <span id={descId} className="text-sm text-muted">
            {description}
          </span>
        ) : null}
      </div>
    </div>
  );
});

export interface RadioCardProps extends ChoiceBaseProps {
  icon?: LucideIcon;
}

/** Whole-card radio for role/intent choices ("I am a student / guardian / teacher"). */
export const RadioCard = forwardRef<HTMLInputElement, RadioCardProps>(function RadioCard(
  { label, description, icon, className, id: givenId, checked, ...rest },
  ref,
) {
  const autoId = useId();
  const id = givenId ?? `rc${autoId.replace(/:/g, '')}`;
  return (
    <label
      htmlFor={id}
      className={cn(
        'relative flex cursor-pointer items-start gap-3 rounded-lg border bg-surface p-4 transition-colors duration-micro',
        'hover:bg-surface-tint has-[:focus-visible]:shadow-focus',
        checked ? 'border-2 border-primary bg-primary-subtle' : 'border-border',
        className,
      )}
    >
      <input
        ref={ref}
        id={id}
        type="radio"
        checked={checked}
        aria-labelledby={`${id}-title`}
        aria-describedby={description ? `${id}-desc` : undefined}
        className="sr-only"
        {...rest}
      />
      {icon ? (
        <span className="flex size-10 shrink-0 items-center justify-center rounded-md bg-surface-sunken text-brand">
          <Icon icon={icon} size={22} />
        </span>
      ) : null}
      <span className="flex flex-col">
        <span id={`${id}-title`} className="font-medium text-primary">
          {label}
        </span>
        {description ? (
          <span id={`${id}-desc`} className="text-sm text-muted">
            {description}
          </span>
        ) : null}
      </span>
    </label>
  );
});

export interface SwitchProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  id?: string;
  className?: string;
}

/**
 * Instant-effect toggle: 44 × 24 track, thumb slides toward the end side
 * (in RTL the "on" thumb sits at the left) — design system §4.10.
 */
export function Switch({ checked, onCheckedChange, label, description, disabled, id: givenId, className }: SwitchProps) {
  const autoId = useId();
  const id = givenId ?? `s${autoId.replace(/:/g, '')}`;
  const labelId = `${id}-label`;
  const descId = description ? `${id}-desc` : undefined;
  return (
    <div className={cn('flex min-h-touch-min items-center justify-between gap-4 py-1', className)}>
      <div className="flex flex-col">
        <span id={labelId} className="text-md text-primary">
          {label}
        </span>
        {description ? (
          <span id={descId} className="text-sm text-muted">
            {description}
          </span>
        ) : null}
      </div>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={labelId}
        aria-describedby={descId}
        disabled={disabled}
        onClick={() => onCheckedChange(!checked)}
        className={cn(
          'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-micro',
          'disabled:cursor-not-allowed disabled:opacity-50',
          checked ? 'bg-primary' : 'bg-switch-off',
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            'absolute top-0.5 size-5 rounded-full bg-paper-0 shadow-1 transition-[inset-inline-start] duration-micro ease-out',
            checked ? 'start-[1.375rem]' : 'start-0.5',
          )}
        />
      </button>
    </div>
  );
}
